import React, { useState, useEffect } from 'react';
import { getToken } from '../utils/storage';
import CustomerLayout from '../components/CustomerLayout';
import CollapsibleTable from '../components/CollapsibleTable';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash2, Pin, Eye, EyeOff, Edit, Plus } from 'lucide-react';
import confirm from '../utils/confirm';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerMessages = () => {
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [showHidden, setShowHidden] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    author_email: '',
    expires_at: '',
    tag: null
  });
  const [screens, setScreens] = useState([]);
  const [selectedScreens, setSelectedScreens] = useState([]);
  const [showAllScreens, setShowAllScreens] = useState(true);
  const [availableTags, setAvailableTags] = useState([]);
  const [maxMessageChars, setMaxMessageChars] = useState(200); // Default 200 characters
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    fetchMessages();
    fetchScreens();
    fetchOrganizationSettings();
  }, []);

  const fetchOrganizationSettings = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API}/organizations/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const tags = response.data.message_tags || [];
      console.log('📋 Loaded TAGs:', tags.length, tags);
      setAvailableTags(tags);
      setMaxMessageChars(response.data.max_message_chars || 200);
    } catch (error) {
      console.error('❌ Failed to fetch organization settings:', error);
    }
  };

  // Handle message text change with character limit
  const handleMessageChange = (newText) => {
    const chars = newText.length;
    setCharCount(chars);
    
    // Only update if within limit
    if (chars <= maxMessageChars) {
      setFormData({...formData, message: newText});
    }
    // If over limit, don't update (textarea maxLength will prevent this)
  };

  const fetchScreens = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API}/screens`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setScreens(response.data);
    } catch (error) {
      console.error('Failed to fetch screens:', error);
    }
  };

  const fetchMessages = async () => {
    try {
      const token = getToken();
      if (!token) {
        console.error('[fetchMessages] No token found');
        toast.error('Ingen autentisering funnet. Prøv å logge ut og inn igjen.');
        return;
      }
      const response = await axios.get(`${API}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      console.error('[fetchMessages] Error:', error.response?.status);
      toast.error(t('customerMessages.errorLoadingMessages'));
    }
  };

  const toggleSticky = async (messageId, currentSticky) => {
    try {
      const token = getToken();
      await axios.put(`${API}/messages/${messageId}`, 
        { is_sticky: !currentSticky },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(currentSticky ? t('customerMessages.messageUnpinned') : t('customerMessages.messagePinned'));
      fetchMessages();
    } catch (error) {
      toast.error(t('customerMessages.errorUpdatingMessage'));
    }
  };

  const toggleApproved = async (messageId, currentApproved) => {
    try {
      const token = getToken();
      await axios.put(`${API}/messages/${messageId}`,
        { is_approved: !currentApproved },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(currentApproved ? t('customerMessages.messageHidden') : t('customerMessages.messageApproved'));
      fetchMessages();
    } catch (error) {
      toast.error(t('customerMessages.errorUpdatingMessage'));
    }
  };

  const handleDelete = async (messageId) => {
    if (!await confirm.danger(t('customerMessages.confirmDeleteMessage'))) return;
    
    try {
      const token = getToken();
      await axios.delete(`${API}/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('customerMessages.messageDeleted'));
      fetchMessages();
    } catch (error) {
      toast.error(t('customerMessages.errorDeletingMessage'));
    }
  };

  const blockEmail = async (email) => {
    if (!await confirm.warning(t('customerMessages.confirmBlockEmail', { email }))) return;
    
    try {
      const token = getToken();
      const formData = new FormData();
      formData.append('email', email);
      
      await axios.post(`${API}/blocked-emails`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('customerMessages.emailBlocked'));
    } catch (error) {
      toast.error(t('customerMessages.errorBlockingEmail'));
    }
  };

  const handleEdit = (message) => {
    setEditingMessage(message);
    setFormData({
      title: message.title,
      message: message.message,
      author_email: message.author_email,
      expires_at: message.end_date ? message.end_date.split('T')[0] : '',
      tag: message.tag || null
    });
    
    // Set character count for existing message
    setCharCount(message.message ? message.message.length : 0);
    
    // Set selected screens
    const msgScreenIds = message.screen_ids || [];
    setSelectedScreens(msgScreenIds);
    setShowAllScreens(msgScreenIds.length === screens.length);
    
    setShowModal(true);
  };

  const handleCreate = () => {
    setEditingMessage(null);
    setFormData({
      title: '',
      message: '',
      author_email: 'admin@byparken.no',
      expires_at: '',
      tag: null
    });
    
    // Reset character count
    setCharCount(0);
    
    // Default: all screens selected
    // Use setTimeout to ensure screens are loaded from state
    setTimeout(() => {
      const allScreenIds = screens.map(s => s.id);
      if (allScreenIds.length > 0) {
        setSelectedScreens(allScreenIds);
        setShowAllScreens(true);
      }
    }, 50);
    
    setShowModal(true);
  };

  const handleScreenToggle = (screenId) => {
    setShowAllScreens(false);
    if (selectedScreens.includes(screenId)) {
      setSelectedScreens(selectedScreens.filter(id => id !== screenId));
    } else {
      setSelectedScreens([...selectedScreens, screenId]);
    }
  };

  const handleShowAllScreens = () => {
    const newShowAll = !showAllScreens;
    setShowAllScreens(newShowAll);
    if (newShowAll) {
      setSelectedScreens(screens.map(s => s.id));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Debug logging
    console.log('[handleSubmit] Starting submit...');
    console.log('[handleSubmit] screens:', screens.length);
    console.log('[handleSubmit] selectedScreens:', selectedScreens);
    console.log('[handleSubmit] showAllScreens:', showAllScreens);
    
    // If screens haven't loaded yet, try fetching them first
    if (screens.length === 0) {
      console.log('[handleSubmit] No screens loaded, fetching...');
      await fetchScreens();
      // After fetching, if still no screens, show error
      if (screens.length === 0) {
        toast.error(t('customerMessages.errorSelectScreen'));
        return;
      }
    }
    
    // If showAllScreens is true but selectedScreens is empty, select all screens
    let screensToUse = selectedScreens;
    if (showAllScreens && screensToUse.length === 0 && screens.length > 0) {
      console.log('[handleSubmit] showAllScreens is true but no screens selected, selecting all...');
      screensToUse = screens.map(s => s.id);
      setSelectedScreens(screensToUse);
    }
    
    // If no screens are selected after screens have loaded, show error
    if (screensToUse.length === 0) {
      console.log('[handleSubmit] No screens selected!');
      toast.error(t('customerMessages.errorSelectScreen'));
      return;
    }
    
    try {
      const token = getToken();
      
      // Map expires_at to end_date for backend compatibility
      const dataToSend = {
        title: formData.title,
        message: formData.message,
        author_email: formData.author_email,
        end_date: formData.expires_at, // Backend expects end_date, not expires_at
        tag: formData.tag,
        screen_ids: screensToUse
      };
      
      console.log('[handleSubmit] Sending data:', dataToSend);
      
      if (editingMessage) {
        // Update existing message
        await axios.put(`${API}/messages/${editingMessage.id}`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(t('customerMessages.messageUpdated'));
      } else {
        // Create new message
        await axios.post(`${API}/messages`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(t('customerMessages.messageCreated'));
      }
      
      setShowModal(false);
      fetchMessages();
    } catch (error) {
      console.error('[handleSubmit] Error:', error);
      if (error.response?.status === 404) {
        toast.error(t('customerMessages.messageNotFound'));
      } else {
        toast.error(t('customerMessages.errorSavingMessage'));
      }
    }
  };

  const filteredMessages = showHidden 
    ? messages 
    : messages.filter(msg => msg.is_approved);

  return (
    <CustomerLayout>
      <div className="admin-header">
        <h1>{t('customerMessages.title')}</h1>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <button 
            className="btn btn-secondary" 
            onClick={() => setShowHidden(!showHidden)}
          >
            {showHidden ? <Eye size={20} /> : <EyeOff size={20} />}
            {showHidden ? t('customerMessages.showActiveOnly') : t('customerMessages.showAll')}
          </button>
          <button className="btn btn-primary" onClick={handleCreate}>
            <Plus size={20} /> {t('customerMessages.newMessage')}
          </button>
        </div>
      </div>

      <CollapsibleTable
        onRowDoubleClick={handleEdit}
        columns={[
          { 
            key: 'title', 
            label: t('customerMessages.tableTitle'),
            minWidth: '150px',
            render: (message) => (
              <>
                {message.is_sticky && <Pin size={16} style={{ color: '#50E3C2', marginRight: '0.5rem' }} />}
                {message.title}
              </>
            )
          },
          { 
            key: 'message', 
            label: t('customerMessages.tableMessage'),
            minWidth: '200px',
            render: (message) => (
              <div style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {message.message}
              </div>
            )
          },
          { 
            key: 'author_email', 
            label: t('customerMessages.tableEmail'),
            minWidth: '180px',
            render: (message) => (
              <>
                <code style={{ fontSize: 'clamp(0.7rem, 1.5vw, 0.875rem)' }}>{message.author_email}</code>
                <button
                  className="btn btn-secondary"
                  style={{ marginLeft: '0.5rem', padding: '0.25rem 0.5rem', fontSize: '0.7rem' }}
                  onClick={() => blockEmail(message.author_email)}
                >
                  {t('customerMessages.block')}
                </button>
              </>
            )
          },
          { 
            key: 'created_at', 
            label: t('customerMessages.tableCreated'),
            minWidth: '100px',
            render: (message) => new Date(message.created_at).toLocaleDateString('no-NO')
          },
          { 
            key: 'end_date', 
            label: t('customerMessages.tableExpiry'),
            minWidth: '120px',
            render: (message) => message.end_date ? new Date(message.end_date).toLocaleDateString('no-NO') : t('customerMessages.noExpiry')
          },
          { 
            key: 'status', 
            label: t('customerMessages.tableStatus'),
            minWidth: '120px',
            render: (message) => (
              <>
                {message.profanity_detected && (
                  <span className="badge badge-danger" style={{ marginRight: '0.5rem', background: '#FF6B6B', color: '#1A1A1A', fontWeight: '600', fontSize: 'clamp(0.65rem, 1.2vw, 0.75rem)' }}>
                    {t('customerMessages.inappropriate')}
                  </span>
                )}
                <span className={message.is_approved ? 'badge badge-success' : 'badge badge-warning'} style={{ fontSize: 'clamp(0.65rem, 1.2vw, 0.75rem)' }}>
                  {message.is_approved ? t('customerMessages.approved') : t('customerMessages.hidden')}
                </span>
              </>
            )
          }
        ]}
        data={filteredMessages}
        actions={(message) => (
          <>
            <button
              className="btn btn-secondary"
              onClick={() => handleEdit(message)}
              title={t('customerMessages.editMessage')}
              style={{ padding: 'clamp(0.4rem, 1vw, 0.5rem)' }}
            >
              <Edit size={window.innerWidth < 1280 ? 14 : 16} />
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => toggleSticky(message.id, message.is_sticky)}
              title={message.is_sticky ? t('customerMessages.unpinMessage') : t('customerMessages.pinMessage')}
              style={message.is_sticky ? { 
                backgroundColor: '#50E3C2', 
                color: '#ffffff',
                border: '1px solid #50E3C2',
                padding: 'clamp(0.4rem, 1vw, 0.5rem)'
              } : { padding: 'clamp(0.4rem, 1vw, 0.5rem)' }}
            >
              <Pin size={window.innerWidth < 1280 ? 14 : 16} />
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => toggleApproved(message.id, message.is_approved)}
              title={message.is_approved ? t('customerMessages.hideMessage') : t('customerMessages.approveMessage')}
              style={{ padding: 'clamp(0.4rem, 1vw, 0.5rem)' }}
            >
              {message.is_approved ? <EyeOff size={window.innerWidth < 1280 ? 14 : 16} /> : <Eye size={window.innerWidth < 1280 ? 14 : 16} />}
            </button>
            <button
              className="btn btn-danger"
              onClick={() => handleDelete(message.id)}
              title={t('customerMessages.deleteMessage')}
              style={{ padding: 'clamp(0.4rem, 1vw, 0.5rem)' }}
            >
              <Trash2 size={window.innerWidth < 1280 ? 14 : 16} />
            </button>
          </>
        )}
      />

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{editingMessage ? t('customerMessages.editMessageTitle') : t('customerMessages.newMessageTitle')}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>{t('customerMessages.formTitle')}</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  required
                />
              </div>
              
              <div className="form-group">
                <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{t('customerMessages.formMessage')}</span>
                  <span style={{ 
                    fontSize: '0.85rem', 
                    color: charCount > maxMessageChars * 0.9 ? '#ff6b6b' : '#888',
                    fontWeight: charCount >= maxMessageChars ? 'bold' : 'normal'
                  }}>
                    {t('customerMessages.charsRemaining', { count: maxMessageChars - charCount })}
                  </span>
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => handleMessageChange(e.target.value)}
                  maxLength={maxMessageChars}
                  rows={5}
                  required
                  placeholder={t('customerMessages.messagePlaceholder', { max: maxMessageChars })}
                />
              </div>

              {availableTags && availableTags.length > 0 && (
                <div className="form-group">
                  <label>{t('customerMessages.selectCategory')}</label>
                  <div style={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: '0.5rem',
                    marginTop: '0.5rem'
                  }}>
                    {availableTags.map((tag, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => setFormData({...formData, tag: formData.tag === tag.name ? null : tag.name})}
                        style={{
                          padding: '0.4rem 0.8rem',
                          fontSize: '0.85rem',
                          borderRadius: '12px',
                          border: formData.tag === tag.name ? `2px solid ${tag.color}` : '2px solid #e0e0e0',
                          background: formData.tag === tag.name ? tag.color : '#FFFFFF',
                          color: formData.tag === tag.name ? tag.text_color : '#2A2A2A',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          fontWeight: formData.tag === tag.name ? '600' : '400'
                        }}
                      >
                        {tag.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>{t('customerMessages.formEmail')}</label>
                <input
                  type="email"
                  value={formData.author_email}
                  onChange={(e) => setFormData({...formData, author_email: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>{t('customerMessages.formExpiry')}</label>
                <input
                  type="date"
                  value={formData.expires_at}
                  onChange={(e) => setFormData({...formData, expires_at: e.target.value})}
                  required
                />
              </div>

              {screens.length >= 1 && (
                <div className="form-group">
                  <label>{t('customerMessages.selectScreens')}</label>
                  
                  {screens.length === 1 ? (
                    // Single screen - just show info text
                    <div style={{ padding: '0.75rem', backgroundColor: '#f0f9f0', borderRadius: '4px', border: '1px solid #d0e9d0' }}>
                      <span style={{ color: '#2d7a2d' }}>
                        ✓ {t('customerMessages.willShowOnScreen', { screenName: screens[0].screen_name || screens[0].name || 'Skjerm' })}
                      </span>
                    </div>
                  ) : (
                    // Multiple screens - show checkboxes
                    <>
                      <div style={{ marginBottom: '0.75rem' }}>
                        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: '0.75rem' }}>
                          <input
                            type="checkbox"
                            checked={showAllScreens}
                            onChange={handleShowAllScreens}
                            style={{ width: 'auto', margin: 0, flexShrink: 0 }}
                          />
                          <span>{t('customerMessages.showOnAllScreens')}</span>
                        </label>
                      </div>

                      {!showAllScreens && (
                        <div style={{ marginTop: '0.75rem' }}>
                          {screens.map(screen => (
                            <div key={screen.id} style={{ marginBottom: '0.75rem' }}>
                              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: '0.75rem' }}>
                                <input
                                  type="checkbox"
                                  checked={selectedScreens.includes(screen.id)}
                                  onChange={() => handleScreenToggle(screen.id)}
                                  style={{ width: 'auto', margin: 0, flexShrink: 0 }}
                                />
                                <span>{screen.screen_name}</span>
                              </label>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                  
                  <small style={{ color: '#999', marginTop: '0.5rem', display: 'block' }}>
                    {t('customerMessages.screenSelectionHelp')}
                  </small>
                </div>
              )}

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                <button type="submit" className="btn btn-primary">
                  {editingMessage ? t('customerMessages.update') : t('customerMessages.create')}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  {t('customerMessages.cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </CustomerLayout>
  );
};

export default CustomerMessages;
